/**
 * Reading the account analytics answer (`GET accounts/analytics/{id}`, OP#3453).
 *
 * The server never invents a number: `null` means "this network does not give
 * it" and `0` is a real zero. These helpers keep that distinction, so the UI can
 * tell a missing metric ("—" and a reason) from a quiet week (a zero).
 */
import { resolvePostPermalink } from "./postPermalink";

/**
 * Networks the server can answer for. Instagram joins with OP#3454; until then
 * its cards say "no analytics" without a request.
 */
export const ANALYTICS_NETWORKS = ["facebook", "tiktok"];

/** Periods the endpoint accepts (90 days is Facebook's per-call maximum). */
export const PERIODS = ["7d", "28d", "90d"];

export const DEFAULT_PERIOD = "28d";

/** Facebook gives insights only to Pages with at least this many followers. */
export const MIN_AUDIENCE = 100;

/** Totals shown as cards, in display order (the ones a network never gives come last). */
export const METRICS = ["followers", "views", "engagements", "posts", "reach"];

/** Codes after which only reconnecting the account helps. */
export const RECONNECT_CODES = ["SESSION_EXPIRED", "IG_INSIGHTS_RECONNECT_REQUIRED"];

const NETWORK_NAMES = {
  facebook: "Facebook",
  tiktok: "TikTok",
  instagram: "Instagram",
  telegram: "Telegram",
  linkedin: "LinkedIn",
  twitter: "X",
  reddit: "Reddit",
  youtube: "YouTube",
  threads: "Threads",
};

// Any code not listed here (META_*, TT_<CODE>, INVALID_PERIOD, a new one) gets the generic text and a retry button.
const ERRORS = {
  ANALYTICS_NOT_SUPPORTED: { key: "analytics.errors.notSupported" },
  ACCOUNT_NOT_FOUND: { key: "analytics.errors.notFound" },
  SESSION_EXPIRED: { key: "analytics.errors.sessionExpired", needsReconnect: true },
  IG_INSIGHTS_RECONNECT_REQUIRED: { key: "analytics.errors.igReconnect", needsReconnect: true },
  META_RATE_LIMIT: { key: "analytics.errors.rateLimit", retryable: true },
  TT_RATE_LIMIT: { key: "analytics.errors.rateLimit", retryable: true },
  META_PERMISSION_DENIED: { key: "analytics.errors.permissionDenied", needsReconnect: true },
  TT_PERMISSION_DENIED: { key: "analytics.errors.permissionDenied", needsReconnect: true },
  BELOW_MIN_AUDIENCE: { key: "analytics.errors.belowMinAudience" },
};

const GENERIC_ERROR = { key: "analytics.errors.generic", retryable: true };

const UNAVAILABLE_KEYS = {
  NOT_PROVIDED: "analytics.unavailable.notProvided",
  BELOW_MIN_AUDIENCE: "analytics.unavailable.belowMinAudience",
  PERMISSION_MISSING: "analytics.unavailable.permissionMissing",
};

/**
 * The name of a network as its owner writes it.
 *
 * @param {string} socialType - Lower-case network id (`facebook`, `tiktok`, …).
 * @returns {string} Display name; the id itself for an unknown network.
 */
export function networkName(socialType) {
  return NETWORK_NAMES[String(socialType || "").toLowerCase()] || String(socialType || "");
}

/**
 * Whether the server has analytics for a network.
 *
 * @param {string} socialType - Lower-case network id.
 * @returns {boolean} True for networks in {@link ANALYTICS_NETWORKS}.
 */
export function supportsAnalytics(socialType) {
  return ANALYTICS_NETWORKS.includes(String(socialType || "").toLowerCase());
}

/**
 * Accounts that can be picked on the dashboard: a network with analytics and
 * not deleted. An account that needs a reconnect stays in the list, so the user
 * sees why its numbers are missing and can fix it from there.
 *
 * @param {Object[]} accounts - Accounts as `accounts` returns them.
 * @returns {Object[]} The accounts worth offering.
 */
export function getAnalyticsAccounts(accounts) {
  return (Array.isArray(accounts) ? accounts : []).filter(
    (a) => a && supportsAnalytics(a.social_type) && a.state !== "deleted",
  );
}

/**
 * Whether a series has anything to draw.
 *
 * @param {Array|null|undefined} series - `[{ date, value }]` or null.
 * @returns {boolean} True for a non-empty array.
 */
export function hasSeries(series) {
  return Array.isArray(series) && series.length > 0;
}

/**
 * Format a UTC day (`YYYY-MM-DD`) for an axis or a tooltip.
 *
 * Days are UTC on the server, so the formatting is pinned to UTC as well;
 * otherwise a viewer west of Greenwich would see every label one day early.
 *
 * @param {string} day - `YYYY-MM-DD` or an ISO timestamp.
 * @param {string} locale - BCP 47 language tag.
 * @returns {string} For example "5 Oct"; the input when it is not a date.
 */
export function formatDay(day, locale = "en") {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day}T00:00:00Z` : day);
  if (Number.isNaN(date.getTime())) return String(day ?? "");
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(date);
}

/**
 * Turn a server series into chart.js data with one dataset.
 *
 * A point whose value is null stays null, so chart.js leaves a gap instead of
 * drawing a false zero.
 *
 * @param {Array|null|undefined} series - `[{ date, value }]`.
 * @param {Object} [options] - Dataset look.
 * @param {string} [options.label] - Dataset label (tooltips).
 * @param {string} [options.color] - Line colour.
 * @param {string} [options.locale] - Language for the day labels.
 * @param {boolean} [options.fill] - Fill under the line.
 * @returns {Object|null} chart.js `data`, or null when there is nothing to draw.
 */
export function seriesToChartData(series, { label = "", color = "#00E5FF", locale = "en", fill = false } = {}) {
  if (!hasSeries(series)) return null;

  return {
    labels: series.map((p) => formatDay(p.date, locale)),
    datasets: [
      {
        label,
        data: series.map((p) => (p.value === undefined ? null : p.value)),
        borderColor: color,
        backgroundColor: fill ? `${color}33` : "transparent",
        fill,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4,
      },
    ],
  };
}

/**
 * Change between two values.
 *
 * @param {number|null|undefined} current - Value now.
 * @param {number|null|undefined} previous - Value at the start of the period.
 * @returns {{abs: number, pct: (number|null)}|null} Absolute change and the
 *   percentage of `previous` (one decimal); `pct` is null when `previous` is 0,
 *   because "from nothing" has no percentage. Null when either side is unknown.
 */
export function calcDelta(current, previous) {
  if (typeof current !== "number" || typeof previous !== "number") return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;

  const abs = current - previous;
  const pct = previous === 0 ? null : Math.round((abs / Math.abs(previous)) * 1000) / 10;
  return { abs, pct };
}

/**
 * Change of one total against the window of the same length right before the
 * period (`previous_totals`).
 *
 * Followers fall back to `totals.followers_delta` when `previous_totals` has no
 * followers. TikTok gives neither (no follower history), so its followers have
 * no change at all.
 *
 * @param {Object|null|undefined} data - The `data` part of the answer.
 * @param {string} key - Key in `totals` (`followers`, `views`, `engagements`, `posts`, …).
 * @returns {{abs: number, pct: (number|null)}|null} The change, or null when
 *   either side is unknown (the card then shows "—" instead of a change).
 */
export function metricChange(data, key) {
  const current = data?.totals?.[key];
  const previous = data?.previous_totals?.[key];
  if (typeof previous === "number") return calcDelta(current, previous);

  if (key === "followers" && typeof current === "number" && typeof data?.totals?.followers_delta === "number") {
    return calcDelta(current, current - data.totals.followers_delta);
  }
  return null;
}

/**
 * Whether one metric has a value, and if not, why.
 *
 * @param {Object|null|undefined} data - The `data` part of the answer.
 * @param {string} key - Key in `totals` (`followers`, `views`, …).
 * @returns {{value: (number|null), available: boolean, reason: (string|null)}}
 *   `reason` is the server's `unavailable[key]` code, or `NOT_PROVIDED` when the
 *   value is null without a code.
 */
export function describeMetric(data, key) {
  const raw = data?.totals?.[key];
  const value = typeof raw === "number" ? raw : null;
  if (value !== null) return { value, available: true, reason: null };
  return { value: null, available: false, reason: data?.unavailable?.[key] || "NOT_PROVIDED" };
}

/**
 * Which metrics a network gave for this account.
 *
 * @param {Object|null|undefined} data - The `data` part of the answer.
 * @returns {Object} `{ metrics: {followers, views, engagements, posts, reach}, followersChart }`
 *   where each metric is {@link describeMetric}'s result and `followersChart`
 *   is `{ available, reason }` for the followers line (TikTok has none).
 */
export function availableMetrics(data) {
  const metrics = {};
  METRICS.forEach((key) => {
    metrics[key] = describeMetric(data, key);
  });

  const followersChart = hasSeries(data?.series?.followers)
    ? { available: true, reason: null }
    : { available: false, reason: data?.unavailable?.["series.followers"] || "NOT_PROVIDED" };

  return { metrics, followersChart };
}

/**
 * Whether the period holds nothing to show: no views, no engagement, no
 * posts, no best posts. Followers are not counted, since they exist without
 * any activity.
 *
 * @param {Object|null|undefined} data - The `data` part of the answer.
 * @returns {boolean} True when the "no data" illustration should replace the charts.
 */
export function isEmptyPeriod(data) {
  if (!data) return false;
  const nonZero = (v) => typeof v === "number" && v > 0;
  const seriesActive = ["views", "engagements"].some(
    (k) => Array.isArray(data.series?.[k]) && data.series[k].some((p) => nonZero(p?.value)),
  );
  const totalsActive = ["views", "reach", "engagements", "posts"].some((k) => nonZero(data.totals?.[k]));
  const hasTop = Array.isArray(data.top_posts) && data.top_posts.length > 0;
  return !seriesActive && !totalsActive && !hasTop;
}

/**
 * Whether the account is too small for the network to report activity.
 *
 * @param {Object|null|undefined} data - The `data` part of the answer.
 * @returns {boolean} True when the server flagged BELOW_MIN_AUDIENCE and there
 *   is no activity data to draw anyway.
 */
export function isBelowMinAudience(data) {
  if (!data?.unavailable) return false;
  const flagged = Object.values(data.unavailable).includes("BELOW_MIN_AUDIENCE");
  return flagged && isEmptyPeriod(data);
}

/**
 * The translation key explaining a missing metric.
 *
 * @param {string|null|undefined} code - Value from `unavailable`.
 * @returns {string} An `analytics.unavailable.*` key; the NOT_PROVIDED one for
 *   a code we do not know.
 */
export function unavailableReasonKey(code) {
  return UNAVAILABLE_KEYS[code] || UNAVAILABLE_KEYS.NOT_PROVIDED;
}

/**
 * Turn a failed response into what the block should say and offer.
 *
 * Failures arrive as the usual envelope with `data.code`; a dropped connection
 * has no code at all and falls into the generic message.
 *
 * @param {Object|null|undefined} response - What `accounts.analytics` returned.
 * @returns {{code: (string|null), key: string, needsReconnect: boolean,
 *   retryable: boolean}} Translation key and the actions that make sense.
 */
export function describeError(response) {
  const code = typeof response?.data?.code === "string" ? response.data.code : null;
  const known = (code && ERRORS[code]) || GENERIC_ERROR;
  return {
    code,
    key: known.key,
    needsReconnect: !!known.needsReconnect,
    retryable: !!known.retryable,
  };
}

/**
 * Where a best post lives on its network.
 *
 * @param {Object} post - An entry of `top_posts`.
 * @param {string} platform - Network id of the account.
 * @returns {string|null} The server's permalink, else one built from the post id
 *   where that is safe, else null (no link rather than a broken one).
 */
export function topPostLink(post, platform) {
  if (!post) return null;
  return resolvePostPermalink({
    status: "success",
    social_type: platform,
    permalink: post.permalink,
    publish_id: post.external_id,
  });
}

/**
 * The address to load a best post's picture from.
 *
 * Our own pictures come as `https://<api host>/api/files/thumbnail/{id}/300/`
 * and need the login cookie, like every other file thumbnail in the app; they
 * are loaded same-origin by path, as `Files.formLink` does. A provider's
 * picture (it can expire) is used as it is. Anything else is not loaded.
 *
 * @param {string|null|undefined} url - `thumbnail_url` of a top post.
 * @returns {string|null} An address for `<img src>`, or null for no picture.
 */
export function thumbnailSrc(url) {
  if (typeof url !== "string" || !/^https?:\/\//i.test(url)) return null;
  try {
    const { pathname, search } = new URL(url);
    return pathname.startsWith("/api/files/thumbnail/") ? `${pathname}${search}` : url;
  } catch (error) {
    return null;
  }
}

/**
 * Number for a card.
 *
 * @param {number|null|undefined} value - The count.
 * @param {string} locale - BCP 47 language tag.
 * @returns {string} Grouped digits, compact from 100 000 up; "—" for no value.
 */
export function formatCount(value, locale = "en") {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const options = Math.abs(value) >= 100000 ? { notation: "compact", maximumFractionDigits: 1 } : {};
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Signed number for a change ("+37", "−12", "0").
 *
 * @param {number} value - The change.
 * @param {string} locale - BCP 47 language tag.
 * @returns {string} The formatted number with an explicit sign.
 */
export function formatSigned(value, locale = "en") {
  return new Intl.NumberFormat(locale, { signDisplay: "exceptZero" }).format(value);
}

/**
 * Signed percentage ("+0.7%").
 *
 * @param {number|null} pct - Percentage as {@link calcDelta} gives it (0.7 means 0.7 %).
 * @param {string} locale - BCP 47 language tag.
 * @returns {string} The formatted percentage; "" when there is none.
 */
export function formatPercent(pct, locale = "en") {
  if (typeof pct !== "number" || !Number.isFinite(pct)) return "";
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(pct / 100);
}
